/**
 * Assignment Overview Tab Component - User Timeline Version
 * Shows assignment timeline, statistics (without user count), and quick actions
 */

import { Box, Typography, Button } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { CalendarIcon, AssignmentIcon } from 'src/v3/core/icons'
import type { SingleUserAssignmentDetails } from 'src/v3/manage/manage.slice'
import { ASSIGNMENTS_URLS } from 'src/v3/manage/constants'

interface OverviewTabProps {
  assignment: SingleUserAssignmentDetails
}

export default function OverviewTab({ assignment }: OverviewTabProps) {
  let navigate = useNavigate()

  let handleViewAssignment = () => {
    navigate(`${ASSIGNMENTS_URLS.INDEX}/${assignment.id}`)
  }

  // Determine assignment status based on due date and completion
  let isOverdue =
    assignment.dueDate && dayjs(assignment.dueDate).isBefore(dayjs(), 'day')
  let isCompleted = assignment.assignmentCompletionPercentage >= 1

  let statusColor = isCompleted
    ? 'success.main'
    : isOverdue
      ? 'error.main'
      : 'info.main'

  let statusText = isCompleted
    ? 'Completed'
    : isOverdue
      ? 'Overdue'
      : 'In Progress'

  let statusSubtext = isCompleted
    ? 'All courses completed'
    : isOverdue
      ? `Due ${dayjs(assignment.dueDate).fromNow()}`
      : 'Currently in progress'

  return (
    <Box>
      <Box className="mb-6">
        <Typography variant="h6" className="mb-4 flex items-center gap-2">
          <CalendarIcon />
          Assignment Timeline
        </Typography>
        <Box className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Box className="text-center">
            <Typography variant="subtitle2">Created</Typography>
            <Typography variant="h6" className="font-semibold mb-1">
              {dayjs(assignment.createdTime).format('MMM D, YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(assignment.createdTime).fromNow()}
            </Typography>
          </Box>
          <Box className="text-center">
            <Typography variant="subtitle2">Due Date</Typography>
            <Typography variant="h6" className="font-semibold mb-1">
              {assignment.dueDate
                ? dayjs(assignment.dueDate).format('MMM D, YYYY')
                : 'No due date'}
            </Typography>
            {assignment.dueDate && (
              <Typography variant="caption" color="text.secondary">
                {dayjs(assignment.dueDate).fromNow()}
              </Typography>
            )}
          </Box>
          <Box className="text-center">
            <Typography variant="subtitle2">Status</Typography>
            <Typography
              variant="h6"
              className="font-semibold mb-1"
              color={statusColor}
            >
              {statusText}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {statusSubtext}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className="mb-6">
        <Typography variant="h6" className="mb-4 flex items-center gap-2">
          <AssignmentIcon />
          Assignment Statistics
        </Typography>
        <Box className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Box
            sx={{
              bgcolor: 'background.paper',
            }}
            className="text-center p-4 rounded"
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'info.main',
              }}
            >
              {assignment.courses.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Courses
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'background.paper',
            }}
            className="text-center p-4 rounded"
          >
            <Typography
              color="success.main"
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              {assignment.totalLength}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total CE Hours
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'background.paper',
            }}
            className="text-center p-4 rounded"
          >
            <Typography
              color="warning.main"
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              {Math.round(assignment.assignmentCompletionPercentage * 100)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className="flex gap-4 justify-center mt-8">
        <Button
          variant="outlined"
          startIcon={<OpenInNew />}
          onClick={handleViewAssignment}
        >
          View Full Assignment
        </Button>
      </Box>
    </Box>
  )
}
